import React, { useState, useEffect } from 'react';
import './EmailSettings.css';

const EmailSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    general: {
      emailProvider: 'gmail', // gmail, outlook, both
      autoRefreshInterval: 300, // seconds
      enableNotifications: true,
      enableDesktopNotifications: true,
      enableVIPAlerts: true
    },
    vipSenders: [],
    trustedDomains: [],
    autoReply: {
      enabled: false,
      message: '',
      keywords: [],
      timeRange: {
        start: '09:00',
        end: '17:00'
      },
      weekdays: [1, 2, 3, 4, 5] // Monday to Friday
    },
    aiInstructions: {
      replyTone: 'professional', // professional, friendly, casual
      replyLength: 'medium', // short, medium, long
      customInstructions: '',
      priorityKeywords: [],
      urgencyKeywords: ['urgent', 'asap', 'immediate', 'emergency'],
      categories: {
        urgent: ['urgent', 'emergency', 'asap', 'critical'],
        important: ['important', 'meeting', 'deadline', 'review'],
        followup: ['follow up', 'check in', 'update', 'status'],
        informational: ['fyi', 'newsletter', 'announcement', 'update']
      }
    }
  });

  // Load settings from localStorage on component mount
  useEffect(() => {
    // ✅ Add try/catch around localStorage.getItem('emailSettings')
    try {
      const savedSettings = localStorage.getItem('emailSettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(parsedSettings);
      }
    } catch (error) {
      console.warn('Failed to load email settings from localStorage:', error);
      // Keep default settings
    }
  }, []);

  // Save settings to localStorage whenever settings change
  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    // ✅ Optionally persist settings to localStorage after save to prevent loss on reload
    try {
      localStorage.setItem('emailSettings', JSON.stringify(newSettings));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
      // Optionally show user notification about save failure
    }
  };

  const handleGeneralSettingChange = (key, value) => {
    const newSettings = {
      ...settings,
      general: {
        ...settings.general,
        [key]: value
      }
    };
    saveSettings(newSettings);
  };

  const addVIPSender = () => {
    const email = prompt('Enter VIP email address:');
    // ✅ Add validation for newVipSender inputs (email format, domain format)
    if (!email || !email.trim()) {
      alert('Please enter an email address.');
      return;
    }
    
    const trimmedEmail = email.trim().toLowerCase();
    
    // Enhanced email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      alert('Please enter a valid email address.');
      return;
    }
    
    // ✅ Use .toLowerCase() when checking for duplicates to avoid case sensitivity issues
    const emailExists = settings.vipSenders.some(sender => 
      sender.email.toLowerCase() === trimmedEmail
    );
    
    if (!emailExists) {
      const newSettings = {
        ...settings,
        vipSenders: [...settings.vipSenders, { 
          email: trimmedEmail, 
          name: trimmedEmail.split('@')[0] 
        }]
      };
      saveSettings(newSettings);
    } else {
      alert('This email is already in your VIP list.');
    }
  };

  const removeVIPSender = (index) => {
    const newSettings = {
      ...settings,
      vipSenders: settings.vipSenders.filter((_, i) => i !== index)
    };
    saveSettings(newSettings);
  };

  const addTrustedDomain = () => {
    const domain = prompt('Enter trusted domain (e.g., company.com):');
    // ✅ Add validation for newTrustedDomain inputs (email format, domain format)
    if (!domain || !domain.trim()) {
      alert('Please enter a domain.');
      return;
    }
    
    const trimmedDomain = domain.trim().toLowerCase();
    
    // Enhanced domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,})+$/;
    let cleanDomain = trimmedDomain.replace(/^https?:\/\//, '').replace(/^www\./, '');
    
    if (!domainRegex.test(cleanDomain)) {
      alert('Please enter a valid domain (e.g., company.com).');
      return;
    }
    
    // ✅ Use .toLowerCase() when checking for duplicates to avoid case sensitivity issues
    const domainExists = settings.trustedDomains.some(existingDomain => 
      existingDomain.toLowerCase() === cleanDomain
    );
    
    if (!domainExists) {
      const newSettings = {
        ...settings,
        trustedDomains: [...settings.trustedDomains, cleanDomain]
      };
      saveSettings(newSettings);
    } else {
      alert('This domain is already in your trusted list.');
    }
  };

  const removeTrustedDomain = (index) => {
    const newSettings = {
      ...settings,
      trustedDomains: settings.trustedDomains.filter((_, i) => i !== index)
    };
    saveSettings(newSettings);
  };

  const handleAutoReplyChange = (key, value) => {
    const newSettings = {
      ...settings,
      autoReply: {
        ...settings.autoReply,
        [key]: value
      }
    };
    saveSettings(newSettings);
  };

  const handleAIInstructionChange = (key, value) => {
    const newSettings = {
      ...settings,
      aiInstructions: {
        ...settings.aiInstructions,
        [key]: value
      }
    };
    saveSettings(newSettings);
  };

  const addKeyword = (type, keyword) => {
    if (!keyword.trim()) return;
    
    const newSettings = {
      ...settings,
      aiInstructions: {
        ...settings.aiInstructions,
        [type]: [...settings.aiInstructions[type], keyword.trim()]
      }
    };
    saveSettings(newSettings);
  };

  const removeKeyword = (type, index) => {
    const newSettings = {
      ...settings,
      aiInstructions: {
        ...settings.aiInstructions,
        [type]: settings.aiInstructions[type].filter((_, i) => i !== index)
      }
    };
    saveSettings(newSettings);
  };

  return (
    <div className="email-settings">
      <div className="settings-header">
        <h1>⚙️ Email Settings</h1>
        <p>Configure your AI email assistant preferences</p>
      </div>

      <div className="settings-tabs">
        <button 
          className={`tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button 
          className={`tab ${activeTab === 'vip' ? 'active' : ''}`}
          onClick={() => setActiveTab('vip')}
        >
          VIP & Trusted
        </button>
        <button 
          className={`tab ${activeTab === 'autoReply' ? 'active' : ''}`}
          onClick={() => setActiveTab('autoReply')}
        >
          Auto-Reply
        </button>
        <button 
          className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          AI Instructions
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'general' && (
          <div className="settings-section">
            <h3>General Settings</h3>
            
            <div className="setting-group">
              <label>Email Provider</label>
              <select 
                value={settings.general.emailProvider}
                onChange={(e) => handleGeneralSettingChange('emailProvider', e.target.value)}
              >
                <option value="gmail">Gmail Only</option>
                <option value="outlook">Outlook Only</option>
                <option value="both">Both Gmail & Outlook</option>
              </select>
            </div>

            <div className="setting-group">
              <label>Auto-refresh Interval (seconds)</label>
              <select 
                value={settings.general.autoRefreshInterval}
                onChange={(e) => handleGeneralSettingChange('autoRefreshInterval', parseInt(e.target.value))}
              >
                <option value={60}>1 minute</option>
                <option value={300}>5 minutes</option>
                <option value={600}>10 minutes</option>
                <option value={1800}>30 minutes</option>
              </select>
            </div>

            <div className="setting-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  checked={settings.general.enableNotifications}
                  onChange={(e) => handleGeneralSettingChange('enableNotifications', e.target.checked)}
                />
                Enable notifications
              </label>
            </div>

            <div className="setting-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  checked={settings.general.enableDesktopNotifications}
                  onChange={(e) => handleGeneralSettingChange('enableDesktopNotifications', e.target.checked)}
                />
                Enable desktop notifications
              </label>
            </div>

            <div className="setting-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  checked={settings.general.enableVIPAlerts}
                  onChange={(e) => handleGeneralSettingChange('enableVIPAlerts', e.target.checked)}
                />
                Enable VIP alerts
              </label>
            </div>
          </div>
        )}

        {activeTab === 'vip' && (
          <div className="settings-section">
            <h3>VIP Senders & Trusted Domains</h3>
            
            <div className="vip-section">
              <div className="section-header">
                <h4>VIP Senders</h4>
                <button onClick={addVIPSender} className="add-btn">+ Add VIP</button>
              </div>
              <div className="vip-list">
                {settings.vipSenders.map((vip, index) => (
                  <div key={index} className="vip-item">
                    <span>{vip.name} ({vip.email})</span>
                    <button onClick={() => removeVIPSender(index)} className="remove-btn">×</button>
                  </div>
                ))}
                {settings.vipSenders.length === 0 && (
                  <p className="empty-state">No VIP senders configured</p>
                )}
              </div>
            </div>

            <div className="trusted-section">
              <div className="section-header">
                <h4>Trusted Domains</h4>
                <button onClick={addTrustedDomain} className="add-btn">+ Add Domain</button>
              </div>
              <div className="trusted-list">
                {settings.trustedDomains.map((domain, index) => (
                  <div key={index} className="trusted-item">
                    <span>{domain}</span>
                    <button onClick={() => removeTrustedDomain(index)} className="remove-btn">×</button>
                  </div>
                ))}
                {settings.trustedDomains.length === 0 && (
                  <p className="empty-state">No trusted domains configured</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'autoReply' && (
          <div className="settings-section">
            <h3>Auto-Reply Settings</h3>
            
            <div className="setting-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  checked={settings.autoReply.enabled}
                  onChange={(e) => handleAutoReplyChange('enabled', e.target.checked)}
                />
                Enable auto-reply
              </label>
            </div>

            <div className="setting-group">
              <label>Auto-reply Message</label>
              <textarea 
                value={settings.autoReply.message}
                onChange={(e) => {
                  // ✅ Prevent updateSetting() from saving overly long or invalid strings (limit textarea to 500 characters)
                  const value = e.target.value;
                  if (value.length <= 500) {
                    handleAutoReplyChange('message', value);
                  }
                }}
                placeholder="Enter your auto-reply message..."
                rows={4}
              />
              <div style={{ 
                fontSize: '12px', 
                color: settings.autoReply.message?.length > 450 ? '#e74c3c' : '#666',
                marginTop: '4px',
                textAlign: 'right'
              }}>
                {(settings.autoReply.message || '').length}/500 characters
              </div>
            </div>

            <div className="time-range">
              <h4>Active Hours</h4>
              <div className="time-inputs">
                <div>
                  <label>Start Time</label>
                  <input 
                    type="time"
                    value={settings.autoReply.timeRange.start}
                    onChange={(e) => handleAutoReplyChange('timeRange', {
                      ...settings.autoReply.timeRange,
                      start: e.target.value
                    })}
                  />
                </div>
                <div>
                  <label>End Time</label>
                  <input 
                    type="time"
                    value={settings.autoReply.timeRange.end}
                    onChange={(e) => handleAutoReplyChange('timeRange', {
                      ...settings.autoReply.timeRange,
                      end: e.target.value
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ai' && (
          <div className="settings-section">
            <h3>AI Response Instructions</h3>
            
            <div className="setting-group">
              <label>Reply Tone</label>
              <select 
                value={settings.aiInstructions.replyTone}
                onChange={(e) => handleAIInstructionChange('replyTone', e.target.value)}
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="casual">Casual</option>
              </select>
            </div>

            <div className="setting-group">
              <label>Reply Length</label>
              <select 
                value={settings.aiInstructions.replyLength}
                onChange={(e) => handleAIInstructionChange('replyLength', e.target.value)}
              >
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="long">Detailed</option>
              </select>
            </div>

            <div className="setting-group">
              <label>Custom Instructions</label>
              <textarea 
                value={settings.aiInstructions.customInstructions}
                onChange={(e) => {
                  // ✅ Prevent updateSetting() from saving overly long or invalid strings (limit textarea to 500 characters)
                  const value = e.target.value;
                  if (value.length <= 500) {
                    handleAIInstructionChange('customInstructions', value);
                  }
                }}
                placeholder="Enter specific instructions for AI responses..."
                rows={4}
              />
              <div style={{ 
                fontSize: '12px', 
                color: settings.aiInstructions.customInstructions?.length > 450 ? '#e74c3c' : '#666',
                marginTop: '4px',
                textAlign: 'right'
              }}>
                {(settings.aiInstructions.customInstructions || '').length}/500 characters
              </div>
            </div>

            <div className="keywords-section">
              <h4>Urgency Keywords</h4>
              <div className="keyword-list">
                {settings.aiInstructions.urgencyKeywords.map((keyword, index) => (
                  <span key={index} className="keyword-tag">
                    {keyword}
                    <button onClick={() => removeKeyword('urgencyKeywords', index)}>×</button>
                  </span>
                ))}
              </div>
              <button 
                onClick={() => {
                  const keyword = prompt('Add urgency keyword:');
                  if (keyword) addKeyword('urgencyKeywords', keyword);
                }}
                className="add-keyword-btn"
              >
                + Add Urgency Keyword
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="settings-footer">
        <div className="save-status">
          Settings are automatically saved
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
